import { Injectable } from "@angular/core";
import {
  IDocument,
  OriginEnum,
} from "app/bundles/requests/shared/models/document";
import { IRawGeojsonRequest } from "app/bundles/requests/shared/models/geojsonRequest";
import { ReturnDDSDocument } from "app/bundles/requests/shared/models/returnDDSDocument";
import { ErrorMessageService } from "app/core/error-message.service";
import { ApiService } from "app/shared/security/api-services/api.service";
import { environment } from "environments/environment";
import { Observable, throwError as observableThrowError } from "rxjs";
import { map, tap } from "rxjs/operators";
import {
  IRawServiceRequestClient,
  IRawServiceRequestDetail,
  IRawServiceRequestSummary,
  IWorkorderRequestDetail,
  IWorkorderRequestShallowDetail,
  IWorkorderRequestSummary,
  PhoneTypeEnum,
  RequestSourceTypeEnum,
  workorderTypeEnum,
} from "../../models/request";
import { RequestApi } from "./request-api.models";

export interface IDDSDocument {
  url?: string;
  description?: string;
  user?: string;
  creationDatetime?: Date;
  origin: "citizen" | "foreman";
}

interface IDDSActivity {
  code: string;
  name: string;
}

interface IDDSDetailsClientPhoneResult {
  type: string;
  number: string;
  extension?: string;
}

interface IDDSDetailsClientResult {
  name: string;
  phones: IDDSDetailsClientPhoneResult[];
  address: string;
  comment: string;
  email: string;
  creationDatetime: Date;
}

interface IDDSDetailsEventResult {
  internalComment: string;
  creationDatetime: Date;
  userCreatedBy: {
    userCode: string;
    firstName: string;
    lastName: string;
  };
}

export interface IRawDDSDetail {
  serviceRequestId: number;
  requestNumber: string;
  status: any;
  comments: {
    code: string;
    "fr-CA": string;
    "en-CA": string;
  };
  requestedDatetime: Date;
  updatedDatetime: Date;
  nbrDaysForResolution: number;
  media: IDDSDocument[];
  formattedAddress: string;
  borough: {
    code: number;
    name: string;
  };
  postalCode: string;
  location: {
    lat: number;
    long: number;
  };
  activity: IDDSActivity;
  nbrOfCommunications: number;
  subject: string;
  isReactivated: boolean;
  isRelaunched: boolean;
  responsible: {
    userName: string;
    userCode: string;
    groupName: string;
    groupId: number;
  };
  clients: IDDSDetailsClientResult[];
  internalComments: IDDSDetailsEventResult[];
}

export interface IRawTelephone {
  type: string;
  number: string;
  post?: string;
}

export interface IRawClient {
  name: string;
  telephones: Array<IRawTelephone>;
  address: string;
  comment: string;
  email: string;
  creationDatetime: Date;
}

export interface IRawWorkOrder {
  id: number; // 1361630
  type: string; // "E", "F"
  requestNumber: string; // "15-227441"
  isRelaunched: boolean;
  isReactivated: boolean;

  description: string;

  clients: IRawClient[];

  responsible: {
    groupName: string;
    groupId: number;
    userName: string;
    userCode: string;
  };

  hasExistingTask: boolean;
  borough: {
    code: number;
    name: string;
  };

  service: {
    code: string; // 3821
    name: string; // "Chantiers PRR"
  };

  status: {
    code: string; // "F"
    name: string; // "Travaux à planifier"
    statusDatetime: Date; // "Travaux à planifier"
  };

  estimatedCompletionDate: Date; // "2015-08-25T11:57:38-05:00"
  source: string;

  formattedAddress: string; // "10330, rue J.-J.-Gagnier, Montréal H2B3A2"

  geometry: {
    type: "Point";
    coordinates: number[]; // [-73.6454928315755, 45.5790241695882]
  };

  creationDatetime: Date; // "2015-08-25T11:57:38-05:00"
  //REM modificationDatetime?: Date;    // "2015-08-25T11:57:38-05:00"

  workorderRequestCreationDatetime: Date;
  serviceRequestId: number;
  hasServiceRequestLinkedTo: boolean;
}

@Injectable()
export class RequestApiService extends RequestApi {
  private _workorderEndpointPath: string =
    environment.apiHost + "/v1/workorders/";
  private _serviceRequestEndpointPath =
    environment.citizenApiHost + "/v1/requests/";

  constructor(
    private apiService: ApiService,
    private errorMessageService: ErrorMessageService,
  ) {
    super();
  }

  public getWorkorderById(
    id: number,
  ): Observable<IWorkorderRequestShallowDetail> {
    const endPoint = `${this._workorderEndpointPath}${id}`;

    return this.apiService.get<IRawWorkOrder>(endPoint).pipe(
      map((res) => {
        const result: IWorkorderRequestShallowDetail = {
          ...res,
          workorderRequestCreationDatetime: new Date(
            res.workorderRequestCreationDatetime,
          ),
          creationDatetime: new Date(res.creationDatetime),
          source: RequestSourceTypeEnum.workorderRequest,
          type: workorderTypeEnum[res.type],
          serviceRequestId: res.serviceRequestId,
          clients: res.clients,
        };
        return result;
      }),
      tap(
        (res) => res,
        (error) => this.errorHandler(error),
      ),
    );
  }

  getServiceRequestById(id: number): Observable<IRawServiceRequestDetail> {
    const endPoint = `${this._serviceRequestEndpointPath}${id}`;

    return this.apiService.get<IRawDDSDetail>(endPoint).pipe(
      map((res) => {
        const clients: IRawServiceRequestClient[] = res.clients.map((c) => ({
          name: c.name,
          phones: c.phones.map((p) => ({
            type: PhoneTypeEnum[p.type],
            number: p.number,
            extension: p.extension,
          })),
          address: c.address,
          comment: c.comment,
          email: c.email,
          creationDatetime: c.creationDatetime,
        }));
        const val: IRawServiceRequestDetail = {
          ...res,
          media: res.media.map((m) => ({
            ...m,
            origin: OriginEnum[m.origin],
          })),
          clients,
        };
        return val;
      }),
      tap({ error: (error) => this.errorHandler(error) }),
    );
  }

  public getDocumentsByWorkorderId(id: number): Observable<IDocument[]> {
    const endPoint = `${this._workorderEndpointPath}${id}/documents`;
    return this.apiService.get<{ items: IDocument[] }>(endPoint).pipe(
      map((res) => res.items),
      tap(
        (result) => result,
        (error) => this.errorHandler(error),
      ),
    );
  }

  public addDocumentByWorkorderId(id: string, document: any): Observable<any> {
    const endPoint = `${this._workorderEndpointPath}${id}/documents`;
    return this.apiService.post(endPoint, JSON.stringify(document)).pipe(
      tap(
        (res) => res,
        (error) => this.errorHandler(error),
      ),
    );
  }

  public getWorkorderGeojsonData(): Observable<IRawGeojsonRequest> {
    const endPoint = `${this._workorderEndpointPath}`;
    return this.apiService.getInWorkorderGeojson(endPoint).pipe(
      tap(
        (res) => res,
        (error) => this.errorHandler(error),
      ),
    );
  }

  public getServiceRequestGeojsonData() {
    const endPoint = this._serviceRequestEndpointPath;
    return this.apiService.getInServiceRequestGeojson(endPoint).pipe(
      map((res) => res),
      tap(
        (res) => res,
        (error) => this.errorHandler(error),
      ),
    );
  }

  public returnDDS(id: number, document: ReturnDDSDocument): Observable<any> {
    const endPoint = `${this._workorderEndpointPath}${id}/service`;
    return this.apiService.patch(endPoint, JSON.stringify(document)).pipe(
      tap(
        (res) => res,
        (error) => this.errorHandler(error),
      ),
    );
  }

  private errorHandler(error): Observable<any> {
    this.errorMessageService.notifyError(error);
    return observableThrowError(error);
  }
}
